import React from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import { Slider } from "./ui/slider";

const EMPTY = { q: "", city: "", country_code: "", transmission: "", fuel: "", seats: "", price: [0, 300000], provides_driver: "", sponsored: "", car_type: "", category: "" };

export default function FilterBar({ filters, setFilters, onApply, sortBy, setSortBy, pillBg = "bg-muted text-muted-foreground hover:bg-muted/80", pillActiveBg = "bg-[#2A9D8F] text-white" }) {
  const { countries } = useCurrency();
  const [openDropdown, setOpenDropdown] = React.useState(null);
  const [showSheet, setShowSheet] = React.useState(false);

  const hasActive = !!(filters.transmission || filters.fuel || filters.q || filters.city || filters.country_code || filters.seats || sortBy !== "default" || filters.price[0] > 0 || filters.price[1] < 300000);

  const isDriverActive = filters.provides_driver === "true" || filters.provides_driver === "false";
  const isCategoryActive = filters.sponsored === "true" || !!filters.category;
  const isTypeActive = !!filters.car_type;
  const noFiltersActive = !isDriverActive && !isCategoryActive && !isTypeActive;

  const go = (f) => { setFilters(f); if (onApply) onApply(f); };
  const pill = (active) => "shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap " + (active ? pillActiveBg : pillBg);

  return (
    <>
      {openDropdown && <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)} />}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => { go({...EMPTY, q: filters.q, city: filters.city, country_code: filters.country_code}); setOpenDropdown(null); }} className={pill(noFiltersActive)}>All</button>

        <div className="relative shrink-0">
          <button type="button" onClick={() => setOpenDropdown(openDropdown === "driver" ? null : "driver")} className={pill(isDriverActive)}>Driver ▾</button>
          {openDropdown === "driver" && (
            <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-xl shadow-lg z-50 min-w-[130px] overflow-hidden">
              {[["true","With Driver"],["false","No Driver"]].map(([val,label]) => (
                <button key={val} type="button" onClick={() => { go({...filters, provides_driver: filters.provides_driver === val ? "" : val}); setOpenDropdown(null); }}
                  className={"w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-muted transition-colors " + (filters.provides_driver === val ? "text-[#2A9D8F] font-semibold" : "")}>{label}</button>
              ))}
            </div>
          )}
        </div>

        <div className="relative shrink-0">
          <button type="button" onClick={() => setOpenDropdown(openDropdown === "type" ? null : "type")} className={pill(isTypeActive)}>Type ▾</button>
          {openDropdown === "type" && (
            <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-xl shadow-lg z-50 min-w-[130px] overflow-hidden">
              {["SUV","Sedan","Hatchback","Van","Pickup"].map((t) => (
                <button key={t} type="button" onClick={() => { go({...filters, car_type: filters.car_type === t ? "" : t, sponsored: ""}); setOpenDropdown(null); }}
                  className={"w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-muted transition-colors " + (filters.car_type === t ? "text-[#2A9D8F] font-semibold" : "")}>{t}</button>
              ))}
            </div>
          )}
        </div>

        <div className="relative shrink-0">
          <button type="button" onClick={() => setOpenDropdown(openDropdown === "category" ? null : "category")} className={pill(isCategoryActive)}>Category ▾</button>
          {openDropdown === "category" && (
            <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-xl shadow-lg z-50 min-w-[140px] overflow-hidden">
              {[["Economy","Economy"],["Standard","Standard"],["Luxury","Luxury"],["sponsored","Sponsored ★"]].map(([key,label]) => (
                <button key={key} type="button" onClick={() => {
                  const f = { ...filters };
                  if (key === "sponsored") { f.sponsored = filters.sponsored === "true" ? "" : "true"; }
                  else { f.category = filters.category === key ? "" : key; }
                  go(f); setOpenDropdown(null);
                }}
                  className={"w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-muted transition-colors " + ((filters.category === key || (key === "sponsored" && filters.sponsored === "true")) ? "text-[#2A9D8F] font-semibold" : "")}>{label}</button>
              ))}
            </div>
          )}
        </div>

        <button type="button" onClick={() => setShowSheet(true)}
          className={"shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ml-auto " + (hasActive ? "border-[#2A9D8F] text-[#2A9D8F] bg-[#2A9D8F]/10" : "border-border text-muted-foreground bg-muted")}>
          <SlidersHorizontal size={12} />{hasActive ? " Filters •" : " Filters"}
        </button>
      </div>

      {showSheet && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => setShowSheet(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-background rounded-t-2xl w-full max-h-[88vh] flex flex-col shadow-xl z-10" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
              <span className="font-display font-semibold text-sm">Filters</span>
              <button type="button" onClick={() => setShowSheet(false)}><X size={16} /></button>
            </div>
            <div className="overflow-y-auto px-4 py-3 space-y-4 flex-1">
              <input type="text" placeholder="Brand / Model (e.g. Toyota, RAV4)" value={filters.q || ""}
                onChange={e => setFilters({...filters, q: e.target.value})}
                className="w-full h-8 px-3 text-xs border border-border rounded-lg bg-background outline-none" />
              <input type="text" placeholder="City (e.g. Kigali)" value={filters.city || ""}
                onChange={e => setFilters({...filters, city: e.target.value})}
                className="w-full h-8 px-3 text-xs border border-border rounded-lg bg-background outline-none" />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Country</div>
                <div className="flex flex-wrap gap-1.5">
                  {[["","Any"],...(countries||[]).map(c => [c.code,c.name])].map(([val,label]) => (
                    <button key={val} type="button" onClick={() => setFilters({...filters, country_code: val})}
                      className={"px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors " + (filters.country_code === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Transmission</div>
                <div className="flex gap-1.5">
                  {[["","Any"],["Automatic","Auto"],["Manual","Manual"]].map(([val,label]) => (
                    <button key={val} type="button" onClick={() => setFilters({...filters, transmission: val})}
                      className={"flex-1 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors " + (filters.transmission === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Fuel</div>
                <div className="flex flex-wrap gap-1.5">
                  {[["","Any"],["Gasoline","Petrol"],["Diesel","Diesel"],["Hybrid","Hybrid"],["Electric","EV"],["Plugin-Hybrid","PHEV"]].map(([val,label]) => (
                    <button key={val} type="button" onClick={() => setFilters({...filters, fuel: val})}
                      className={"px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors " + (filters.fuel === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Min seats</div>
                <div className="flex gap-1.5">
                  {[["","Any"],["2","2+"],["4","4+"],["5","5+"],["7","7+"],["15","15+"],["30","30+"]].map(([val,label]) => (
                    <button key={val} type="button" onClick={() => setFilters({...filters, seats: val})}
                      className={"flex-1 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors " + (filters.seats === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Price/day (RWF)</div>
                  <span className="text-[10px] text-muted-foreground">{(filters.price||[0,300000])[0].toLocaleString()} - {(filters.price||[0,300000])[1].toLocaleString()}</span>
                </div>
                <Slider min={0} max={300000} step={1000} value={filters.price||[0,300000]} onValueChange={v => setFilters({...filters, price: v})} className="mt-1" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Sort by</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {[["default","Default"],["price_asc","Price up"],["price_desc","Price down"],["rating","Top Rated"]].map(([val,label]) => (
                    <button key={val} type="button" onClick={() => setSortBy(val)}
                      className={"py-1.5 rounded-lg text-[11px] font-semibold border transition-colors text-center " + (sortBy === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border shrink-0 flex gap-2">
              <button type="button" onClick={() => { if(onApply) onApply(filters); setShowSheet(false); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#2A9D8F] text-white">Apply</button>
              <button type="button" onClick={() => { const f={...EMPTY}; setFilters(f); setSortBy("default"); if(onApply) onApply(f); setShowSheet(false); }}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold border border-border text-muted-foreground">Clear</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
